import { useEffect, useState } from "react";
import { CheckCheck, X, Loader2, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPE_LABEL = {
  inventory_pending: { label: "Pending testing", color: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/40" },
  service_pending: { label: "Pending service", color: "bg-[#FFE4E6] text-[#9F1239] border-[#F43F5E]/40" },
  lead_followup: { label: "Lead follow-up", color: "bg-[#DBEAFE] text-[#1E40AF] border-[#3B82F6]/40" },
  sale_feedback_6m: { label: "6-month feedback", color: "bg-[#E0F2FE] text-[#075985] border-[#0EA5E9]/40" },
  sale_maintenance_12m: { label: "Annual maintenance", color: "bg-[#E0E7FF] text-[#3730A3] border-[#6366F1]/40" },
};

export default function Notifications() {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data || []);
    } catch (e) { console.error("Failed to fetch notifications:", e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchNotifs(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/notifications/${id}/action`, { action });
      setNotifs((p) => p.filter((n) => n.id !== id));
      toast.success(action === "completed" ? "Marked completed" : "Dismissed");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Reminders</div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Notifications</h1>
        <p className="text-sm text-[#475569] mt-1">Follow-ups, feedback calls and maintenance reminders — all in one inbox.</p>
      </div>

      {loading ? (
        <div className="text-sm text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
      ) : notifs.length === 0 ? (
        <div className="border border-dashed border-[#E2E8F0] rounded-lg p-12 text-center" data-testid="notifications-empty">
          <BellRing className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
          <div className="text-sm font-medium">You're all caught up</div>
          <div className="text-xs text-[#64748B] mt-1">New reminders appear every 5 days for open leads, and at 6m / 12m after each sale.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {notifs.map((n) => {
            const meta = TYPE_LABEL[n.type] || { label: n.type, color: "bg-slate-100 text-slate-700 border-slate-300" };
            return (
              <div key={n.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-start gap-4" data-testid={`notif-row-${n.id}`}>
                <div className={`px-2 py-0.5 border rounded-full text-[10px] font-mono uppercase tracking-wider shrink-0 ${meta.color}`}>
                  {meta.label}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0A0A0A]">{n.title}</div>
                  {n.subtitle && <div className="text-xs text-[#475569] mt-0.5">{n.subtitle}</div>}
                  {n.detail && <div className="text-xs text-[#64748B] mt-1">{n.detail}</div>}
                  <div className="text-[10px] font-mono text-[#94A3B8] mt-2">{n.due_days} days · {n.priority} priority</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => act(n.id, "dismissed")} data-testid={`dismiss-${n.id}`}>
                    <X className="w-3.5 h-3.5" /> Dismiss
                  </Button>
                  <Button size="sm" className="gap-1 h-8 bg-green-600 hover:bg-green-700 text-white" onClick={() => act(n.id, "completed")} data-testid={`complete-${n.id}`}>
                    <CheckCheck className="w-3.5 h-3.5" /> Completed
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
